import React from "react";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { toast } from "sonner";

// types
import { useAppDispatch } from "../../../store/hooks";
import type { ErrorRegister, Register } from "../../../store/types/auth";
import type { ErrorWarning } from "../../../store/types/misc";
import { validateEmail } from "../../../services/formatter/validate";
import { register } from "../../../services/api/auth";
import axiosInstance from "../../../utility/axiosInstance";
import { setLoading, stopLoading } from "../../../store/slices/loading";
import { clearMessage } from "../../../store/slices/message";

const getRouteForStep = (step: string, orgId: string): string => {
  switch (step) {
    case "FILL_ONBOARDING_FORM":
      return `/onboarding-form?Id=${orgId}`;
    case "SIGN_MSA":
      return `/master-service-agreement?Id=${orgId}`;
    case "SIGN_SLA":
      return `/service-level-agreement?Id=${orgId}`;
    case "SIGN_DPA":
      return `/data-processing-agreement?Id=${orgId}`;
    case "MAKE_70_PAYMENT":
      return `/payment-selection?Id=${orgId}`;
    case "REVIEW_GO_LIVE":
      return `/go-live?Id=${orgId}`;
    case "MAKE_30_PAYMENT":
      return `/payment-selection?Id=${orgId}`;
    default:
      return `/onboarding-form?Id=${orgId}`;
  }
};

const useRegister = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [emailExist, emailExistSet] = React.useState<ErrorWarning>("");
  const [isExistingUser, setIsExistingUser] = React.useState<boolean>(false);
  const [orgId, setOrgId] = React.useState<string | null>(null);
  const [currentStep, setCurrentStep] = React.useState<string | null>(null);
  const [warningModal, setWarningModal] = React.useState<{
    open: boolean;
    message: string;
    organizationId: string | null;
  }>({
    open: false,
    message: "",
    organizationId: null,
  });

  const [user, userSet] = React.useState<Register>({
    name: "",
    representativeFirstName: "",
    representativeLastName: "",
    representativeEmail: "",
    representativeRole: "",
    contactPhone: "",
    address: "",
    registrationNo: "",
    companySize: "",
    atsSystem: "",
    challengeDescription: "",
    referralSource: "",
    parentDomain: "",
  });

  const [error, errorSet] = React.useState<ErrorRegister>({
    name: "",
    representativeFirstName: "",
    representativeLastName: "",
    representativeEmail: "",
    representativeRole: "",
    challengeDescription: "",
    companySize: "",
  });

  const fetchResumptionInfo = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/organizations/${id}/resumption-info`);
      if (response.data?.currentStep) {
        setCurrentStep(response.data.currentStep);
      }
    } catch (err) {
      console.error("Error fetching resumption info:", err);
    }
  };

  const handleResendContinuationLink = async () => {
    if (!orgId) {
      toast.error("Organization ID not found.");
      return;
    }
    dispatch(setLoading());
    try {
      const response = await axiosInstance.post(`/organizations/${orgId}/resend-resumption-email`);
      if (response.data?.success) {
        toast.success("Continuation email resent successfully!");
        const step = response.data.step || response.data.resumeStep || response.data.currentStep || currentStep;
        if (step) {
          navigate(getRouteForStep(step, orgId));
        }
      } else {
        toast.error(response.data?.message || "Failed to resend resumption email.");
      }
    } catch (err) {
      if (isAxiosError(err)) {
        toast.error(err.response?.data?.message || "Failed to resend resumption email.");
      } else {
        toast.error("Failed to resend resumption email.");
      }
    } finally {
      dispatch(stopLoading());
    }
  };

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlOrgId = searchParams.get("Id") || searchParams.get("orgId");
    if (urlOrgId) {
      setOrgId(urlOrgId);
      setIsExistingUser(true);
      fetchResumptionInfo(urlOrgId);
    }
  }, []);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    userSet((prev) => ({ ...prev, [name]: value }));
    if (name === "email") {
      emailExistSet("");
    }
  };

  const onChangeHandle = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    userSet((prev) => ({ ...prev, [name]: value }));
  };

  const onChangeTextbox = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    userSet((prev) => ({ ...prev, [name]: value }));
    errorSet((prev) => ({ ...prev, [name]: "" }));
  };

  // Validation function - now validates trimmed values
  const onValidateUser = (trimmedUser: Register): boolean => {
    let error: boolean = true;
    if (!trimmedUser.name) {
      errorSet((prev) => ({ ...prev, name: "warning" }));
      error = false;
    }
    if (
      !trimmedUser.representativeEmail ||
      !validateEmail(trimmedUser.representativeEmail) ||
      emailExist
    ) {
      errorSet((prev) => ({ ...prev, representativeEmail: "warning" }));
      error = false;
    }
    if (!trimmedUser.representativeFirstName) {
      errorSet((prev) => ({ ...prev, representativeFirstName: "warning" }));
      error = false;
    }
    if (!trimmedUser.representativeLastName) {
      errorSet((prev) => ({ ...prev, representativeLastName: "warning" }));
      error = false;
    }
    if (!trimmedUser.representativeRole) {
      errorSet((prev) => ({ ...prev, representativeRole: "warning" }));
      error = false;
    }
    if (!trimmedUser.companySize) {
      errorSet((prev) => ({ ...prev, companySize: "warning" }));
      error = false;
    }
    if (!trimmedUser.challengeDescription) {
      errorSet((prev) => ({ ...prev, challengeDescription: "warning" }));
      error = false;
    }
    return error;
  };

  // submit function with whitespace trimming
  const onSubmit = async () => {
    // Create a copy of user data with trimmed email and password
    const trimmedUser = {
      ...user,
      representativeEmail: user.representativeEmail.trim(),
    };

    // Update the state with trimmed values for visual feedback
    userSet(trimmedUser);

    // Validate with trimmed values
    if (onValidateUser(trimmedUser)) {
      const response = await register(dispatch, trimmedUser);

      const responseStr = JSON.stringify(response || {}).toLowerCase();
      const isExisting =
        responseStr.includes("already exist") ||
        responseStr.includes("already_exist") ||
        responseStr.includes("duplicate") ||
        responseStr.includes("existing");

      // Handle the backend "warning" status (email already registered)
      const isWarning = response?.status === "warning";
      const extractedOrgIdFromWarning =
        response?.data?.organizationId || response?.organizationId || null;

      if (isWarning && extractedOrgIdFromWarning) {
        dispatch(clearMessage());
        setIsExistingUser(true);
        setOrgId(extractedOrgIdFromWarning);
        fetchResumptionInfo(extractedOrgIdFromWarning);
        setWarningModal({
          open: true,
          message:
            response?.message ||
            "Email already registered. Click the resend button below to resend the onboarding resumption link to your email.",
          organizationId: extractedOrgIdFromWarning,
        });
        return;
      }

      if (isExisting) {
        setIsExistingUser(true);
        dispatch(clearMessage());

        const findOrgId = (obj: any): string | null => {
          if (!obj || typeof obj !== "object") return null;
          if (obj.organizationId) return String(obj.organizationId);
          if (obj.orgId) return String(obj.orgId);
          if (obj.id) return String(obj.id);
          if (obj._id) return String(obj._id);
          for (const key in obj) {
            if (obj[key] && typeof obj[key] === "object") {
              const res = findOrgId(obj[key]);
              if (res) return res;
            }
          }
          return null;
        };

        const extractedOrgId = findOrgId(response);

        if (extractedOrgId) {
          setOrgId(extractedOrgId);
          fetchResumptionInfo(extractedOrgId);
          setWarningModal({
            open: true,
            message: "An account with this email already exists. Click the button below to resend the continuation link.",
            organizationId: extractedOrgId,
          });
        } else {
          toast.error("Account already exists, but organization ID could not be retrieved.");
        }
      }
    }
  };

  const closeWarningModal = () => {
    setWarningModal({ open: false, message: "", organizationId: null });
  };

  return {
    user,
    emailExist,
    error,
    isExistingUser,
    warningModal,
    closeWarningModal,
    onChangeHandler,
    onChangeHandle,
    onChangeTextbox,
    onSubmit,
    handleResendContinuationLink,
  };
};

export default useRegister;
